import type { SubmitInfo, SubmitInfoAction, SubmitStepInfo } from "./types";

import {
  type APIDeletedImage,
  type EnergyAnalysisFiles,
  type FileWithInfo,
  type FileWithInfoLocal,
  isFileFromLocal,
} from "../../model/types";

export function submitInfoReducer(state: SubmitInfo, action: SubmitInfoAction): SubmitInfo {
  switch (action.type) {
    case "MARK_IN_PROGRESS_UPLOADS_ERRORED":
      return {
        ...state,
        files: state.files.map((r) => (r.status === "progress" ? { ...r, status: "error" } : r)),
        images: state.images.map((r) => (r.status === "progress" ? { ...r, status: "error" } : r)),
      };
    case "PREPARE_SUBMIT": {
      const { base, formData } = action;
      return {
        buildingInfo: base.buildingInfo,
        deletedAPIImages: deletedAPIImagesFromForm([], formData.deletedAPIImages),
        files: fileRowsFromForm([], formData.files),
        images: imageRowsFromForm([], formData.images.filter(isFileFromLocal)),
        projectCreation: base.projectCreation,
      };
    }
    case "SYNC_FROM_FORM":
      return {
        ...state,
        files: fileRowsFromForm(state.files, action.formData.files),
        images: imageRowsFromForm(state.images, action.formData.images.filter(isFileFromLocal)),
      };
    case "UPDATE_BUILDING_INFO":
      return { ...state, buildingInfo: action.updater(state.buildingInfo) };
    case "UPDATE_PROJECT":
      return { ...state, projectCreation: action.updater(state.projectCreation) };
    case "UPSERT_UPLOAD_ROW": {
      const key =
        action.list === "deletedAPIImages" ? "deletedAPIImages" : action.list === "files" ? "files" : "images";
      return {
        ...state,
        [key]: upsertUploadRowInList(state[key], action.file, action.percentage, action.status, action.errorMessage),
      };
    }
    default:
      return state;
  }
}

function deletedAPIImagesFromForm(
  prev: SubmitStepInfo[],
  deletedAPIImages: APIDeletedImage[] | undefined
): SubmitStepInfo[] {
  return (deletedAPIImages ?? []).map((image, index) => {
    const prevRow = prev.find((r) => r.id === image.id) ?? {};
    return { id: image.id, name: `Image ${index + 1}`, status: undefined, ...prevRow }; // TODO: Add image name
  });
}

function fileRowsFromForm(prev: SubmitStepInfo[], files: EnergyAnalysisFiles | undefined): SubmitStepInfo[] {
  return (Object.values(files ?? {}) ?? []).reduce<SubmitStepInfo[]>((acc, sectionFiles) => {
    const rows =
      sectionFiles?.map((file) => {
        const prevRow = prev.find((r) => r.id === file.id) ?? {};
        return { id: file.id, name: file.file.name, status: undefined, ...prevRow };
      }) ?? [];
    return [...acc, ...rows];
  }, []);
}

function imageRowsFromForm(prev: SubmitStepInfo[], images: FileWithInfoLocal[] | undefined): SubmitStepInfo[] {
  return (
    images?.map((image) => {
      const prevRow = prev.find((r) => r.id === image.id) ?? {};
      return { id: image.id, name: image.file.name, status: undefined, ...prevRow };
    }) ?? []
  );
}

function upsertUploadRowInList(
  rows: SubmitStepInfo[],
  file: FileWithInfo,
  percentage: number,
  status: SubmitStepInfo["status"],
  errorMessage?: string
): SubmitStepInfo[] {
  const next = [...rows];
  const index = next.findIndex((row) => row.id === file.id);
  if (index !== -1) {
    next[index] = { ...next[index], errorMessage, percentage, status };
  } else {
    next.push({
      errorMessage,
      id: file.id,
      name: isFileFromLocal(file) ? file.file.name : "tempName",
      percentage,
      status,
    });
  }
  return next;
}
